import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/Button';
import Input from '@/components/Input';
import { Tag } from '@/components/Tag';
import { isValidEmail } from '../utils/isValidEmail';

interface InviteAgentModalProps {
  open: boolean;
  onClose: () => void;
  onInvite: (data: { name: string; email: string }) => void;
  isSubmitting?: boolean;
  errorMessage?: string | null;
}

export default function InviteAgentModal({
  open,
  onClose,
  onInvite,
  isSubmitting = false,
  errorMessage = null,
}: InviteAgentModalProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');

  const handleSubmit = () => {
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    if (!trimmedName || !isValidEmail(trimmedEmail)) return;
    onInvite({ name: trimmedName, email: trimmedEmail });
  };

  const handleClose = () => {
    setName('');
    setEmail('');
    onClose();
  };

  const isEmailFormatInvalid = email.trim() !== '' && !isValidEmail(email);
  const isValid = name.trim() !== '' && email.trim() !== '' && !isEmailFormatInvalid;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          handleClose();
        }
      }}
    >
      <DialogContent className="w-[640px] rounded">
        <DialogHeader className="items-start space-y-0 px-lg! py-md!">
          <DialogTitle className="text-base font-bold">新規エージェントを招待</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-md p-lg">
          {errorMessage && (
            <div className="rounded border border-solid border-danger bg-red-50 p-sm">
              <p className="text-body-sm text-danger">{errorMessage}</p>
            </div>
          )}
          {/* エージェント名 */}
          <div className="flex w-[360px] flex-col gap-xs">
            <div className="flex items-center gap-xs">
              <label className="text-heading-xxs">エージェント名</label>
              <Tag color="danger" size="sm" className="font-bold!">
                必須
              </Tag>
            </div>
            <div className="flex flex-col gap-xxs">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="LMSエージェント"
                className="w-full"
              />
            </div>
          </div>

          {/* メールアドレス */}
          <div className="flex w-[360px] flex-col gap-xs">
            <div className="flex items-center gap-xs">
              <label className="text-heading-xxs">メールアドレス</label>
              <Tag color="danger" size="sm" className="font-bold!">
                必須
              </Tag>
            </div>
            <div className="flex flex-col gap-xxs">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@example.com"
                className="w-full"
              />
              {isEmailFormatInvalid && (
                <p role="alert" className="text-xs text-danger">
                  メールアドレスの形式が正しくありません
                </p>
              )}
              <p className="text-xs text-sub">このメールアドレスに招待メールが送信されます</p>
            </div>
          </div>
        </div>

        <DialogFooter className="justify-end gap-md px-lg! py-md! sm:space-x-0!">
          <Button color="secondary" size="md" onClick={handleClose} disabled={isSubmitting}>
            キャンセル
          </Button>
          <Button
            color="primary"
            size="md"
            onClick={handleSubmit}
            disabled={!isValid || isSubmitting}
          >
            {isSubmitting ? '送信中...' : '招待メールを送信'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
